/**
 * Bet configs (GET/POST/PATCH/DELETE /api/configs) and their resolution (`/configs/trace`,
 * `/configs/game-configs`, `/configs/schemas`). Configs use the games permission bits.
 *
 * Traps handled here (rgs-api-contract):
 * - list and detail embed `Operator` (with ApiSalt) and `Game` (with EngineApiKey): the schemas keep
 *   only ids and names, so the secrets never reach the UI;
 * - writes don't check min ≤ default ≤ max or that the operator/game/currency exist: the form does;
 * - Settlement/Theme are accepted only on brand-level rows (game 0, currency `*`): never sent otherwise;
 * - every write flushes the shared Redis cache, so there are no bulk write loops here.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { jsonColumn } from '@/api/zod-helpers';
import type { FieldMeta } from '@/lib/diff';
import { formatMoney, formatNumber } from '@/lib/format';
import { filterParam, listSearchSchema, toPagedQuery } from '@/lib/url-state';
import { describeBrand, describeCurrency, isBrandLevelScope } from './levels';

// ---- value schemas ------------------------------------------------------------------------------

/** FLOAT bet columns: numbers (sometimes strings), null when the row doesn't set them. */
const nullableNumber = z.union([z.number(), z.string(), z.null(), z.undefined()]).transform((v, ctx) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) {
    ctx.addIssue({ code: 'custom', message: 'expected a number' });
    return z.NEVER;
  }
  return n;
});

const jsonRecord = jsonColumn(z.record(z.string(), z.unknown()).nullish()).transform((v) => v ?? null);

const jsonNumberList = jsonColumn(z.array(z.union([z.number(), z.string()])).nullish()).transform((v) =>
  v ? v.map(Number).filter((n) => Number.isFinite(n)) : null,
);

const named = z
  .object({ Id: z.coerce.number().int(), Name: z.string().nullish() })
  .nullish()
  .transform((v) => (v ? { Id: v.Id, Name: v.Name ?? null } : null));

export const configRowSchema = z.object({
  Id: z.coerce.number().int(),
  OperatorId: z.coerce.number().int(),
  Brand: z.string(),
  GameId: z.coerce.number().int(),
  Currency: z.string(),
  MinBet: nullableNumber,
  MaxBet: nullableNumber,
  DefaultBet: nullableNumber,
  MaxExp: nullableNumber,
  Description: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
  CurrencyMultipliers: jsonRecord,
  CreditValues: jsonNumberList,
  Promotions: jsonRecord,
  Settlement: jsonRecord,
  Theme: jsonRecord,
  // Relations: ids and names only (the API also sends Operator.ApiSalt and Game.EngineApiKey).
  Operator: named,
  Game: named,
  BrandData: named,
});

export type ConfigRow = z.infer<typeof configRowSchema>;

/** Currency code → multiplier, as numbers (the column is a free JSON object). */
export function multipliersOf(value: Record<string, unknown> | null): Record<string, number> | null {
  if (!value) return null;
  const out: Record<string, number> = {};
  for (const [code, raw] of Object.entries(value)) {
    const n = Number(raw);
    if (Number.isFinite(n)) out[code] = n;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Empty JSON counts as "not set" in the resolver (`JSON_LENGTH = 0` falls through). */
export const isSetJson = (value: unknown) =>
  value !== null &&
  value !== undefined &&
  (Array.isArray(value) ? value.length > 0 : typeof value === 'object' ? Object.keys(value).length > 0 : true);

// ---- formatting -----------------------------------------------------------------------------------

/** Config bet columns are EUR units (floats). */
export const formatEur = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : formatMoney(Math.round(value * 100), 'EUR');

export const formatMultipliers = (value: Record<string, number> | null) =>
  value
    ? Object.entries(value)
        .map(([code, n]) => `${code} ×${formatNumber(n, { maxFractionDigits: 6 })}`)
        .join(', ')
    : '—';

export const formatCreditValues = (value: number[] | null) =>
  value && value.length > 0 ? value.map((n) => formatNumber(n, { maxFractionDigits: 6 })).join(', ') : '—';

// ---- list -----------------------------------------------------------------------------------------

/**
 * The server's sortable allow-list (module-fields.config.ts, `configs`), minus the names that aren't
 * columns of the table: sorting by `Status`, `CreatedAt`, `Game.Name` or `BrandData.Name` is a 400.
 */
export const CONFIG_SORT_FIELDS = [
  'Id',
  'OperatorId',
  'Brand',
  'GameId',
  'Currency',
  'MinBet',
  'MaxBet',
  'DefaultBet',
] as const;

export const DEFAULT_CONFIG_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

export const configsSearchSchema = listSearchSchema({
  sortFields: CONFIG_SORT_FIELDS,
  filters: {
    operator: filterParam.number(),
    /** 0 = rows for every game. */
    game: filterParam.number(),
    /** `*` = rows for every currency. */
    currency: filterParam.string(),
  },
});

export type ConfigsSearch = z.infer<typeof configsSearchSchema>;

export function configsPagedQuery(search: ConfigsSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Brand', operator: 'contains', value: search.q });
  if (search.operator !== undefined) filters.push({ field: 'OperatorId', operator: 'eq', value: search.operator });
  if (search.game !== undefined) filters.push({ field: 'GameId', operator: 'eq', value: search.game });
  if (search.currency) filters.push({ field: 'Currency', operator: 'eq', value: search.currency });
  return toPagedQuery(search, { defaultSort: DEFAULT_CONFIG_SORT, filters });
}

const listSchema = pagedSchema(configRowSchema);

export interface ResolveRequest {
  operatorId: number;
  brand: string;
  gameId: number;
  currency: string;
}

export const configsKeys = {
  all: ['configs'] as const,
  list: (query: PagedQuery) => ['configs', 'list', query] as const,
  detail: (id: number) => ['configs', 'detail', id] as const,
  trace: (r: ResolveRequest) => ['configs', 'trace', r] as const,
  resolved: (r: ResolveRequest) => ['configs', 'resolved', r] as const,
  brandLevel: (operatorId: number) => ['configs', 'brand-level', operatorId] as const,
  scope: (s: ScopeLookup) => ['configs', 'scope', s] as const,
  schemas: ['configs', 'schemas'] as const,
};

export const configsListQuery = (search: ConfigsSearch) => {
  const query = configsPagedQuery(search);
  return queryOptions({
    queryKey: configsKeys.list(query),
    queryFn: ({ signal }) => http('/configs', { query: encodePagedQuery(query), signal }, listSchema),
    placeholderData: keepPreviousData,
  });
};

export const configQuery = (id: number) =>
  queryOptions({
    queryKey: configsKeys.detail(id),
    queryFn: ({ signal }) => http(`/configs/${id}`, { signal }, configRowSchema),
  });

// ---- scope lookups ----------------------------------------------------------------------------------

export interface ScopeLookup {
  operatorId: number;
  brand: string;
  gameId: number;
  currency: string;
}

/** The row with exactly this scope, if any (the table has a unique key on it). */
export const scopeRowQuery = (s: ScopeLookup) =>
  queryOptions({
    queryKey: configsKeys.scope(s),
    queryFn: async ({ signal }) => {
      const query: PagedQuery = {
        skip: 0,
        take: 1,
        filters: [
          { field: 'OperatorId', operator: 'eq', value: s.operatorId },
          { field: 'Brand', operator: 'eq', value: s.brand },
          { field: 'GameId', operator: 'eq', value: s.gameId },
          { field: 'Currency', operator: 'eq', value: s.currency },
        ],
      };
      const page = await http('/configs', { query: encodePagedQuery(query), signal }, listSchema);
      // `eq` on Brand compares with MySQL's case-insensitive collation: confirm the exact match here.
      return page.data.find((r) => r.Brand === s.brand && r.GameId === s.gameId && r.Currency === s.currency) ?? null;
    },
  });

/** An operator's brand-level rows (every game, every currency): where Settlement and Theme come from. */
export const brandLevelRowsQuery = (operatorId: number) =>
  queryOptions({
    queryKey: configsKeys.brandLevel(operatorId),
    queryFn: async ({ signal }) => {
      const query: PagedQuery = {
        skip: 0,
        take: 1000,
        filters: [
          { field: 'OperatorId', operator: 'eq', value: operatorId },
          { field: 'GameId', operator: 'eq', value: 0 },
          { field: 'Currency', operator: 'eq', value: '*' },
        ],
      };
      const page = await http('/configs', { query: encodePagedQuery(query), signal }, listSchema);
      return page.data;
    },
  });

// ---- resolution -------------------------------------------------------------------------------------

const traceConfigSchema = z.object({
  Id: z.coerce.number().int(),
  Brand: z.string(),
  GameId: z.coerce.number().int(),
  GameName: z.string().nullish(),
  Currency: z.string(),
  MinBet: nullableNumber,
  MaxBet: nullableNumber,
  DefaultBet: nullableNumber,
  MaxExp: nullableNumber,
  CurrencyMultipliers: jsonRecord,
  CreditValues: jsonNumberList,
  Promotions: jsonRecord,
});

export type TraceConfig = z.infer<typeof traceConfigSchema>;

export const TRACED_COLUMNS = [
  'MinBet',
  'MaxBet',
  'DefaultBet',
  'MaxExp',
  'CurrencyMultipliers',
  'CreditValues',
  'Promotions',
] as const;

export type TracedColumn = (typeof TRACED_COLUMNS)[number];

export const traceSchema = z.object({
  rows: z.array(z.object({ level: z.string(), levelLabel: z.string(), config: traceConfigSchema })),
  winners: z.record(z.string(), z.coerce.number().int().nullable()),
  unconsulted: z.array(traceConfigSchema),
});

export type ConfigTrace = z.infer<typeof traceSchema>;

const path = (r: ResolveRequest) =>
  `${r.operatorId}/${encodeURIComponent(r.brand)}/${r.gameId}/${encodeURIComponent(r.currency)}`;

export const traceQuery = (r: ResolveRequest) =>
  queryOptions({
    queryKey: configsKeys.trace(r),
    queryFn: ({ signal }) => http(`/configs/trace/${path(r)}`, { signal }, traceSchema),
  });

/** What the generator produced for the player: range bounds and ladder amounts are player-currency cents. */
const generatedSchema = z.object({
  gameConfigs: z.object({
    betConfig: z
      .object({
        min: z.coerce.number().optional(),
        max: z.coerce.number().optional(),
        default: z.coerce.number().optional(),
        step: z.coerce.number().optional(),
        maxExposure: z.coerce.number().optional(),
        creditValues: z.array(z.coerce.number()).optional(),
      })
      .optional(),
  }),
  currencyMultiplier: z.coerce.number(),
  availableBetAmounts: z.array(z.coerce.number()).default([]),
  alert: z.string().optional(),
});

const resolvedGameSchema = z.object({
  gameId: z.coerce.number().int(),
  gameName: z.string(),
  betConfigs: z.object({
    min_bet: nullableNumber,
    max_bet: nullableNumber,
    default_bet: nullableNumber,
    max_exp: nullableNumber,
    currency_multipliers: jsonRecord,
    credit_values: jsonNumberList,
    promotions: jsonRecord,
  }),
  gameConfigs: generatedSchema,
});

export const resolvedSchema = z.object({
  data: z.array(z.object({ currency: z.string(), games: z.array(resolvedGameSchema) })),
  brandLevel: z.object({ settlement: jsonRecord, theme: jsonRecord }),
});

export type ResolvedConfig = z.infer<typeof resolvedSchema>;
export type ResolvedGame = z.infer<typeof resolvedGameSchema>;

export const resolvedQuery = (r: ResolveRequest) =>
  queryOptions({
    queryKey: configsKeys.resolved(r),
    queryFn: ({ signal }) => http(`/configs/game-configs/${path(r)}`, { signal }, resolvedSchema),
  });

// ---- JSON schemas of the config columns -------------------------------------------------------------

export interface JsonSchema {
  type?: string | string[];
  title?: string;
  description?: string;
  properties?: Record<string, JsonSchema>;
  additionalProperties?: boolean | JsonSchema;
  required?: string[];
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
  items?: JsonSchema;
}

const jsonSchemaShape: z.ZodType<JsonSchema> = z.lazy(() =>
  z.object({
    type: z.union([z.string(), z.array(z.string())]).optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    properties: z.record(z.string(), jsonSchemaShape).optional(),
    additionalProperties: z.union([z.boolean(), jsonSchemaShape]).optional(),
    required: z.array(z.string()).optional(),
    enum: z.array(z.unknown()).optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    items: jsonSchemaShape.optional(),
  }),
);

export const configSchemasQuery = queryOptions({
  queryKey: configsKeys.schemas,
  queryFn: ({ signal }) =>
    http(
      '/configs/schemas',
      { signal },
      z.object({ settlement: jsonSchemaShape, theme: jsonSchemaShape, promotions: jsonSchemaShape }),
    ),
  staleTime: 60 * 60_000,
});

export type ConfigSchemas = { settlement: JsonSchema; theme: JsonSchema; promotions: JsonSchema };

// ---- writes -----------------------------------------------------------------------------------------

/** What the form edits. `null` = not set at this level: the next level down applies. */
export interface ConfigValues extends Record<string, unknown> {
  MinBet: number | null;
  MaxBet: number | null;
  DefaultBet: number | null;
  MaxExp: number | null;
  Description: string | null;
  CurrencyMultipliers: Record<string, number> | null;
  CreditValues: number[] | null;
  Promotions: Record<string, unknown> | null;
  Settlement: Record<string, unknown> | null;
  Theme: Record<string, unknown> | null;
}

export interface ConfigScopeValues extends Record<string, unknown> {
  OperatorId: number;
  Brand: string;
  GameId: number;
  Currency: string;
}

export const toConfigValues = (row: ConfigRow): ConfigValues => ({
  MinBet: row.MinBet,
  MaxBet: row.MaxBet,
  DefaultBet: row.DefaultBet,
  MaxExp: row.MaxExp,
  Description: row.Description,
  CurrencyMultipliers: multipliersOf(row.CurrencyMultipliers),
  CreditValues: row.CreditValues && row.CreditValues.length > 0 ? row.CreditValues : null,
  Promotions: isSetJson(row.Promotions) ? row.Promotions : null,
  Settlement: isSetJson(row.Settlement) ? row.Settlement : null,
  Theme: isSetJson(row.Theme) ? row.Theme : null,
});

const eurField = (key: string, label: string): FieldMeta => ({
  key,
  label,
  format: (v) => formatEur(typeof v === 'number' ? v : Number(v)),
});

export const CONFIG_VALUE_FIELDS: FieldMeta[] = [
  eurField('MinBet', 'Min bet (EUR)'),
  eurField('MaxBet', 'Max bet (EUR)'),
  eurField('DefaultBet', 'Default bet (EUR)'),
  eurField('MaxExp', 'Max exposure (EUR)'),
  {
    key: 'CurrencyMultipliers',
    label: 'Currency multipliers',
    format: (v) => formatMultipliers(v as Record<string, number>),
  },
  { key: 'CreditValues', label: 'Credit values', format: (v) => formatCreditValues(v as number[]) },
  { key: 'Promotions', label: 'Promotions' },
  { key: 'Settlement', label: 'Settlement' },
  { key: 'Theme', label: 'Theme' },
  { key: 'Description', label: 'Description' },
];

export const CONFIG_SCOPE_FIELDS: FieldMeta[] = [
  { key: 'OperatorId', label: 'Operator', format: (v) => `#${String(v)}` },
  { key: 'Brand', label: 'Brand', format: (v) => describeBrand(String(v)) },
  { key: 'GameId', label: 'Game', format: (v) => (Number(v) === 0 ? 'All games' : `#${String(v)}`) },
  { key: 'Currency', label: 'Currency', format: (v) => describeCurrency(String(v)) },
];

/** The wire body. Settlement/Theme only on brand-level rows, where the server accepts them. */
export function toWireBody(
  values: Partial<ConfigValues & ConfigScopeValues>,
  scope: { GameId: number; Currency: string },
) {
  const body: Record<string, unknown> = { ...values };
  if (!isBrandLevelScope(scope)) {
    delete body.Settlement;
    delete body.Theme;
  }
  for (const key of ['CurrencyMultipliers', 'Promotions', 'Settlement', 'Theme', 'CreditValues'] as const) {
    if (key in body && !isSetJson(body[key])) body[key] = null;
  }
  if ('Description' in body && body.Description === '') body.Description = null;
  return body;
}

export function useCreateConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ConfigValues & ConfigScopeValues) =>
      http('/configs', { method: 'POST', body: toWireBody(values, values) }, configRowSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: configsKeys.all });
    },
  });
}

export function useUpdateConfig(row: Pick<ConfigRow, 'Id' | 'GameId' | 'Currency'>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<ConfigValues>) =>
      http(`/configs/${row.Id}`, { method: 'PATCH', body: toWireBody(patch, row) }, configRowSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: configsKeys.all });
    },
  });
}

export function useDeleteConfig(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/configs/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: configsKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: configsKeys.all });
    },
  });
}
